// Browser-side review and single-attempt guard. Only our own admin API is called.
export interface CostCheckDisplay {
  checkedAt: number; validUntil: number; maximumLossBps: number; indicativeLossBps: number; minimumReturnRaw: string;
}
export interface SubmissionRecord {
  batchId: string; chainId: number; escrow: string; orderHash: string; permitDigest: string; nonce: string;
  actor: string; intentHash: string; requestHash: string; state: 'prepared' | 'sending' | 'accepted' | 'uncertain';
  preparedAt: number; expiresAt: number; environment: string; maySubmit: boolean;
  outcome: { code?: string; httpStatus?: number; checkedAt?: number } | null;
  lookup: { code?: string; apiStatus?: string; checkedAt?: number; transactionHash?: string } | null;
  readiness?: { costCheck?: CostCheckDisplay; protocol?:{reactor:string;blockNumber:string;pinsHash:string;orderType:string;upgradeTrustRemains:boolean} };
  request: { method: string; url: string; body: { encodedOrder: string; signature: string; chainId: number; quoteId: string } };
}
export interface SubmissionBatch {
  id: string; stage: string; escrow: string | null; fill: unknown; inputAmount: string | null;
  plan: { chainId?: number; rewardAsset: string; settlementAsset: string; minReward: string; expiry: number };
  quote: { orderHash: string; deadline: number; recipient?: string; swapper?: string; minOutput?: string } | null;
  submission?: SubmissionRecord | null;
}
export interface SubmissionContext { revision: string; actor: string; configured: boolean; batch: SubmissionBatch | null }
export type SubmissionAction = 'prepare' | 'confirm' | 'recover';
const equal = (a: string | null | undefined, b: string | null | undefined) => !!a && !!b && a.toLowerCase() === b.toLowerCase();
const hash = (value: unknown) => typeof value === 'string' && /^0x[0-9a-f]{64}$/i.test(value);
export const submissionAttemptKey = (s: SubmissionRecord) => `receipt:pcsx-submission:${s.chainId}:${s.escrow.toLowerCase()}:${s.batchId}:${s.intentHash.toLowerCase()}`;
export function submissionAvailability(c: SubmissionContext, now: number, attempted = false) {
  const b = c.batch, s = b?.submission;
  const eligible = !!b && b.plan.chainId === 56 && b.stage === 'authorized' && !b.fill && !!b.quote && !!b.escrow && b.plan.expiry * 1000 > now && b.quote.deadline * 1000 > now;
  const expired = !!s && (!Number.isSafeInteger(s.expiresAt) || s.expiresAt <= now);
  const bound = !!b && !!s && s.batchId === b.id && s.chainId === 56 && equal(s.escrow, b.escrow) && equal(s.orderHash, b.quote?.orderHash);
  const canPrepare = c.configured === true && eligible && (!s || (s.state === 'prepared' && expired && bound));
  const canConfirm = c.configured === true && eligible && bound && s!.state === 'prepared' && !expired && s!.maySubmit === true && equal(s!.actor, c.actor) && hash(s!.intentHash) && !attempted;
  // Local recover POST invokes only an upstream GET; it cannot send another order.
  const canRecover = !!s && (s.state !== 'prepared' || attempted);
  return { eligible, expired, bound, canPrepare, canConfirm, canRecover, attempted };
}
export class SubmissionUiError extends Error { constructor(public code: string) { super(code); } }
export function createSubmissionActions(options: {
  context(): SubmissionContext; checkWallet(): Promise<boolean>;
  post(url: string, body: unknown): Promise<{ submission: SubmissionRecord; batch: SubmissionBatch }>;
  now?: () => number; storage?: Pick<Storage, 'getItem' | 'setItem'>;
}) {
  const attempted = new Set<string>(); let busy = false;
  function wasAttempted(s?: SubmissionRecord | null) {
    if (!s) return false; const key = submissionAttemptKey(s);
    if (attempted.has(key)) return true;
    try { return options.storage?.getItem(key) === 'attempted'; } catch { return false; }
  }
  function availability() { const c = options.context(); return submissionAvailability(c, (options.now || Date.now)(), wasAttempted(c.batch?.submission)); }
  const snapshot = (c: SubmissionContext) => JSON.stringify(c);
  async function run(action: SubmissionAction) {
    if (busy) throw new SubmissionUiError('SUBMISSION_UI_BUSY');
    const initial = options.context(), stamp = snapshot(initial), s = initial.batch?.submission;
    const permitted = (v: ReturnType<typeof submissionAvailability>) => action === 'prepare' ? v.canPrepare : action === 'confirm' ? v.canConfirm : v.canRecover;
    if (!initial.actor || !initial.batch || !permitted(availability())) throw new SubmissionUiError('SUBMISSION_UI_REFRESH_REQUIRED');
    busy = true;
    try {
      if (!await options.checkWallet()) throw new SubmissionUiError('SUBMISSION_AUTHORIZATION_REQUIRED');
      if (snapshot(options.context()) !== stamp || !permitted(availability())) throw new SubmissionUiError('SUBMISSION_UI_CHANGED');
      if (action === 'confirm') {
        const key = submissionAttemptKey(s!); attempted.add(key);
        try { options.storage?.setItem(key, 'attempted'); } catch { /* The server independently enforces one irreversible attempt. */ }
      }
      const result = await options.post(`/api/admin/procurement/${encodeURIComponent(initial.batch.id)}/submission/${action}`, action === 'confirm' ? { intentHash: s!.intentHash } : {});
      // Late responses may be persisted by the server; never apply them to another page/session.
      if (snapshot(options.context()) !== stamp) return null;
      if (!result.submission || result.submission.batchId !== initial.batch.id || result.batch.id !== initial.batch.id) throw new SubmissionUiError('SUBMISSION_UI_RESPONSE_MISMATCH');
      return result;
    } finally { busy = false; }
  }
  return { run, availability, wasAttempted, get busy() { return busy; } };
}
